import { useState, useEffect, useCallback, useRef } from "react";
import { Campo } from "../../../services/tipos";
import {
  VideoRecordingState,
  EXTENSOES_IMAGEM_PERMITIDAS,
  EXTENSOES_ARQUIVO_PERMITIDAS,
  EXTENSOES_VIDEO_PERMITIDAS,
  TAMANHO_MAXIMO_IMAGEM,
  TAMANHO_MAXIMO_ARQUIVO,
  TAMANHO_MAXIMO_VIDEO,
} from "../types";

interface UseFileUploadProps {
  show: boolean;
  campos: Campo[];
  entidadeSelecionada?: any;
  entidade: any;
  setEntidade: (fn: (prev: any) => any) => void;
  setErrors: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  onImageRemoved?: (campo: string) => void;
  onFileRemoved?: (campo: string) => void;
  onVideoRemoved?: (campo: string) => void;
}

export function useFileUpload(props: UseFileUploadProps) {
  // Use refs for values that change frequently to avoid callback invalidation
  const entidadeRef = useRef(props.entidade);
  entidadeRef.current = props.entidade;
  const imagePreviewRef = useRef<Record<string, string>>({});
  const filePreviewRef = useRef<Record<string, { name: string; size: string; url: string }>>({});

  const [imagePreview, setImagePreview] = useState<Record<string, string>>({});
  const [filePreview, setFilePreview] = useState<Record<string, { name: string; size: string; url: string }>>({});
  const [videoPreview, setVideoPreview] = useState<Record<string, string>>({});
  const [dragActive, setDragActive] = useState<Record<string, boolean>>({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const [videoRecording, setVideoRecording] = useState<VideoRecordingState>({
    isRecording: false,
    mediaRecorder: null,
    stream: null,
    recordedChunks: [],
  });
  const [currentVideoField, setCurrentVideoField] = useState('');

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return 'Clique no ícone para baixar';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Initialize previews from entity data
  useEffect(() => {
    if (props.show) {
      if (props.entidadeSelecionada) {
        const initialImagePreviews = props.campos.reduce((acc, campo) => {
          if (campo.tipo === "image" && props.entidadeSelecionada[campo.nome]) {
            acc[campo.nome] = props.entidadeSelecionada[campo.nome];
          }
          return acc;
        }, {} as Record<string, string>);
        setImagePreview(initialImagePreviews);

        const initialFilePreviews = props.campos.reduce((acc, campo) => {
          if (campo.tipo === "file" && props.entidadeSelecionada[campo.nome]) {
            acc[campo.nome] = {
              url: props.entidadeSelecionada[campo.nome],
              name: props.entidadeSelecionada[`${campo.nome}_nome`] || 'Arquivo',
              size: formatFileSize(props.entidadeSelecionada[`${campo.nome}_tamanho`] || 0),
            };
          }
          return acc;
        }, {} as Record<string, { name: string; size: string; url: string }>);
        setFilePreview(initialFilePreviews);

        const initialVideoPreviews = props.campos.reduce((acc, campo) => {
          if (campo.tipo === "video" && props.entidadeSelecionada[campo.nome]) {
            acc[campo.nome] = props.entidadeSelecionada[campo.nome];
          }
          return acc;
        }, {} as Record<string, string>);
        setVideoPreview(initialVideoPreviews);
      } else {
        setImagePreview({});
        setFilePreview({});
        setVideoPreview({});
      }
    }
  }, [props.show, props.entidadeSelecionada]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      Object.values(imagePreview).forEach(url => {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
      Object.values(videoPreview).forEach(url => {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
      if (currentImage.startsWith('blob:')) URL.revokeObjectURL(currentImage);
      if (videoRecording.stream) {
        videoRecording.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [imagePreview, videoPreview, currentImage, videoRecording]);

  const validarDuracaoVideo = (arquivo: File, duracaoMaxSegundos: number): Promise<{ valido: boolean; mensagem?: string }> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        const duracao = video.duration;
        if (duracao > duracaoMaxSegundos) {
          resolve({
            valido: false,
            mensagem: `O vídeo ultrapassa a duração máxima de ${Math.floor(duracaoMaxSegundos / 60)} minutos.`,
          });
        } else {
          resolve({ valido: true });
        }
      };
      video.src = URL.createObjectURL(arquivo);
    });
  };

  const validarArquivo = async (
    arquivo: File,
    extensoesPermitidas: string[],
    tamanhoMaximo: number
  ): Promise<{ valido: boolean; mensagem?: string }> => {
    const extensao = arquivo.name.split('.').pop()?.toLowerCase();
    if (!extensao || !extensoesPermitidas.includes(extensao)) {
      return { valido: false, mensagem: `Extensão não permitida. Use: ${extensoesPermitidas.join(', ')}` };
    }
    if (arquivo.size <= 0) {
      return { valido: false, mensagem: `O arquivo está vazio. Envie um arquivo com conteúdo.` };
    }
    if (arquivo.size > tamanhoMaximo) {
      const tamanhoEmMB = (tamanhoMaximo / (1024 * 1024)).toFixed(0);
      return { valido: false, mensagem: `Arquivo muito grande. Tamanho máximo: ${tamanhoEmMB}MB` };
    }
    return { valido: true };
  };

  const validarVideo = async (
    arquivo: File,
    extensoesPermitidas: string[],
    tamanhoMaximo: number
  ): Promise<{ valido: boolean; mensagem?: string }> => {
    const extensao = arquivo.name.split('.').pop()?.toLowerCase();
    if (!extensao || !extensoesPermitidas.includes(extensao)) {
      return { valido: false, mensagem: `Extensão de vídeo não permitida. Use: ${extensoesPermitidas.join(', ')}` };
    }
    const resultadoDuracao = await validarDuracaoVideo(arquivo, 120);
    if (!resultadoDuracao.valido) {
      return { valido: false, mensagem: `Coloque um vídeo de até 2 minutos de duração.` };
    }
    if (arquivo.size <= 0) {
      return { valido: false, mensagem: `O arquivo de vídeo está vazio.` };
    }
    if (arquivo.size > tamanhoMaximo) {
      const tamanhoEmMB = (tamanhoMaximo / (1024 * 1024)).toFixed(0);
      return { valido: false, mensagem: `Vídeo muito grande. Tamanho máximo: ${tamanhoEmMB}MB` };
    }
    return { valido: true };
  };

  const compressImage = (file: File, maxWidth = 1024, quality = 0.7): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (file.size <= 1 * 1024 * 1024) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx!.imageSmoothingQuality = 'high';
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) { resolve(file); return; }
              const compressedFile = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => resolve(file);
      };
      reader.onerror = () => resolve(file);
    });
  };

  const handleImageChange = useCallback(async (campo: string, file: File) => {
    if (!file) return;
    const validacao = await validarArquivo(file, EXTENSOES_IMAGEM_PERMITIDAS, TAMANHO_MAXIMO_IMAGEM);
    if (!validacao.valido) {
      props.setErrors((prev) => ({ ...prev, [campo]: validacao.mensagem || 'Imagem inválida' }));
      return;
    }
    try {
      let processedFile = file;
      if (file.size > 500 * 1024) {
        processedFile = await compressImage(file, 1024, 0.7);
      }
      if (imagePreview[campo] && imagePreview[campo].startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview[campo]);
      }
      const imageUrl = URL.createObjectURL(processedFile);
      setImagePreview((prev) => ({ ...prev, [campo]: imageUrl }));
      props.setEntidade((prev: any) => ({ ...prev, [campo]: processedFile }));
      props.setErrors((prev) => { const n = { ...prev }; delete n[campo]; return n; });
    } catch (error) {
      const imageUrl = URL.createObjectURL(file);
      setImagePreview((prev) => ({ ...prev, [campo]: imageUrl }));
      props.setEntidade((prev: any) => ({ ...prev, [campo]: file }));
    }
  }, [imagePreview]);

  const handleFileChange = useCallback(async (campo: string, file: File) => {
    if (!file) return;
    const validacao = await validarArquivo(file, EXTENSOES_ARQUIVO_PERMITIDAS, TAMANHO_MAXIMO_ARQUIVO);
    if (!validacao.valido) {
      props.setErrors((prev) => ({ ...prev, [campo]: validacao.mensagem || 'Arquivo inválido' }));
      return;
    }
    if (filePreview[campo]?.url && filePreview[campo].url.startsWith('blob:')) {
      URL.revokeObjectURL(filePreview[campo].url);
    }
    const fileUrl = URL.createObjectURL(file);
    setFilePreview((prev) => ({
      ...prev,
      [campo]: { name: file.name, size: formatFileSize(file.size), url: fileUrl },
    }));
    props.setEntidade((prev: any) => ({
      ...prev,
      [campo]: file,
      [`${campo}_nome`]: file.name,
      [`${campo}_tamanho`]: file.size,
    }));
    props.setErrors((prev) => { const n = { ...prev }; delete n[campo]; return n; });
  }, [filePreview]);

  const handleVideoChange = useCallback(async (campo: string, file: File) => {
    if (!file) return;
    const validacao = await validarVideo(file, EXTENSOES_VIDEO_PERMITIDAS, TAMANHO_MAXIMO_VIDEO);
    if (!validacao.valido) {
      props.setErrors((prev) => ({ ...prev, [campo]: validacao.mensagem || 'Vídeo inválido' }));
      return;
    }
    if (videoPreview[campo] && videoPreview[campo].startsWith('blob:')) {
      URL.revokeObjectURL(videoPreview[campo]);
    }
    const videoUrl = URL.createObjectURL(file);
    setVideoPreview((prev) => ({ ...prev, [campo]: videoUrl }));
    props.setEntidade((prev: any) => ({ ...prev, [campo]: file }));
    props.setErrors((prev) => { const n = { ...prev }; delete n[campo]; return n; });
  }, [videoPreview]);

  const handleImageRemove = useCallback((campo: string) => {
    if (currentImage === imagePreview[campo] || currentImage === entidadeRef.current[campo]) {
      setShowImageModal(false);
      setCurrentImage('');
    }
    if (imagePreview[campo] && imagePreview[campo].startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview[campo]);
    }
    setImagePreview((prev) => { const n = { ...prev }; delete n[campo]; return n; });
    props.setEntidade((prev: any) => ({ ...prev, [campo]: null }));
    props.setErrors((prev) => { const n = { ...prev }; delete n[campo]; return n; });
    props.onImageRemoved?.(campo);
  }, [imagePreview, currentImage, props.onImageRemoved]);

  const handleFileRemove = useCallback((campo: string) => {
    if (filePreview[campo]?.url.startsWith('blob:')) {
      URL.revokeObjectURL(filePreview[campo].url);
    }
    setFilePreview((prev) => { const n = { ...prev }; delete n[campo]; return n; });
    props.setEntidade((prev: any) => ({
      ...prev,
      [campo]: null,
      [`${campo}_nome`]: null,
      [`${campo}_tamanho`]: null,
    }));
    props.setErrors((prev) => { const n = { ...prev }; delete n[campo]; return n; });
    props.onFileRemoved?.(campo);
  }, [filePreview, props.onFileRemoved]);

  const handleVideoRemove = useCallback((campo: string) => {
    if (videoPreview[campo] && videoPreview[campo].startsWith('blob:')) {
      URL.revokeObjectURL(videoPreview[campo]);
    }
    setVideoPreview((prev) => { const n = { ...prev }; delete n[campo]; return n; });
    props.setEntidade((prev: any) => ({ ...prev, [campo]: null }));
    props.setErrors((prev) => { const n = { ...prev }; delete n[campo]; return n; });
    props.onVideoRemoved?.(campo);
  }, [videoPreview, props.onVideoRemoved]);

  const handleDrag = useCallback((e: React.DragEvent, campo: string, active: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive((prev) => ({ ...prev, [campo]: active }));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, campo: string, tipo: 'image' | 'file' | 'video') => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive((prev) => ({ ...prev, [campo]: false }));
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (tipo === 'image') {
        if (file.type.startsWith('image/')) handleImageChange(campo, file);
        else props.setErrors((prev) => ({ ...prev, [campo]: 'O arquivo não é uma imagem válida' }));
      } else if (tipo === 'file') {
        handleFileChange(campo, file);
      } else if (tipo === 'video') {
        if (file.type.startsWith('video/')) handleVideoChange(campo, file);
        else props.setErrors((prev) => ({ ...prev, [campo]: 'O arquivo não é um vídeo válido' }));
      }
    }
  }, [handleImageChange, handleFileChange, handleVideoChange]);

  const handleDownloadFile = useCallback(async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Erro ao baixar arquivo');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Erro no download:', error);
      alert('Erro ao baixar o arquivo.');
    }
  }, []);

  const handleViewImage = useCallback((campo: string) => {
    const imagemAtual = imagePreview[campo] || entidadeRef.current[campo];
    if (imagemAtual) {
      setCurrentImage(imagemAtual);
      setShowImageModal(true);
    }
  }, [imagePreview]);

  const startVideoRecording = useCallback(async (campo: string) => {
    try {
      setCurrentVideoField(campo);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
      const recordedChunks: Blob[] = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) recordedChunks.push(event.data);
      };
      mediaRecorder.onstop = () => {
        const videoBlob = new Blob(recordedChunks, { type: 'video/webm' });
        const videoFile = new File([videoBlob], `recorded-video-${Date.now()}.webm`, { type: 'video/webm' });
        handleVideoChange(campo, videoFile);
        stream.getTracks().forEach(track => track.stop());
      };
      setVideoRecording({ isRecording: true, mediaRecorder, stream, recordedChunks });
      mediaRecorder.start();
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      alert('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  }, [handleVideoChange]);

  const stopVideoRecording = useCallback(() => {
    if (videoRecording.mediaRecorder && videoRecording.isRecording) {
      videoRecording.mediaRecorder.stop();
      setVideoRecording(prev => ({ ...prev, isRecording: false }));
    }
  }, [videoRecording]);

  const cancelVideoRecording = useCallback(() => {
    if (videoRecording.mediaRecorder && videoRecording.isRecording) {
      videoRecording.mediaRecorder.stop();
    }
    if (videoRecording.stream) {
      videoRecording.stream.getTracks().forEach(track => track.stop());
    }
    setVideoRecording({ isRecording: false, mediaRecorder: null, stream: null, recordedChunks: [] });
    setCurrentVideoField('');
  }, [videoRecording]);

  const resetPreviews = useCallback(() => {
    setImagePreview({});
    setFilePreview({});
    setVideoPreview({});
  }, []);

  return {
    imagePreview,
    filePreview,
    videoPreview,
    dragActive,
    showImageModal,
    setShowImageModal,
    currentImage,
    setCurrentImage,
    videoRecording,
    currentVideoField,
    handleImageChange,
    handleFileChange,
    handleVideoChange,
    handleImageRemove,
    handleFileRemove,
    handleVideoRemove,
    handleDrag,
    handleDrop,
    handleDownloadFile,
    handleViewImage,
    startVideoRecording,
    stopVideoRecording,
    cancelVideoRecording,
    resetPreviews,
  };
}
